import { useState, useEffect } from 'react';
import { PointsEntityState, PointsWidgetsEntityState } from 'src/state/entities/points/reducer';
import {
  WidgetThemesProps,
  WidgetTheme,
  WidgetPoints,
  WidgetsTemplate,
} from '../types/pointsWidgetstypes';
import {
  ConditionalItem,
  DateTimeItem,
  MappingItem,
  RangeTimeItem,
  WidgetListType,
  WidgetsTypes,
} from '../types/widgetTypes';
import widgetsAvalon from 'src/components/ui/pointsUI/widgets/widget-data/widgetsAvalon.json';
import widgetsEnvy from 'src/components/ui/pointsUI/widgets/widget-data/widgetsEnvy.json';
import { PointsProps } from '../types/types';
import { Site } from 'src/models';
import { EQUIPMENT_MAKE } from '../types/types';

export const EQUIPMENT_TYPE = {
  inverter: 'Inverter',
};

const WIDGET_TITLE_MAP: Record<WidgetsTypes, string> = {
  dateTime: 'Date and time',
  rangeTime: 'Range time',
  conditional: 'Conditional',
  mapping: 'Mapping',
  default: 'Default',
};

const getMatchingTemplate = (
  point: PointsProps,
  widgetList: (DateTimeItem | RangeTimeItem | ConditionalItem | MappingItem)[]
) => {
  return widgetList.find((item) => item.address === point.address);
};

const addWidgets = (
  pointsData: PointsEntityState,
  equipmentMake?: keyof typeof EQUIPMENT_MAKE
): PointsWidgetsEntityState => {
  if (!pointsData || !pointsData.points) {
    return {
      error: false,
      loading: false,
      saving: false,
      points: {},
      siteId: -1,
      lastUpdated: '-',
      errorMessage: '',
    };
  }

  const result: WidgetThemesProps = {};

  if (!equipmentMake) {
    return {
      error: true,
      loading: false,
      saving: false,
      points: {},
      siteId: -1,
      lastUpdated: '-',
      errorMessage: 'No equipment make found',
    };
  }

  const widgetList: WidgetListType =
    equipmentMake === EQUIPMENT_MAKE.avalon
      ? (widgetsAvalon as WidgetListType)
      : (widgetsEnvy as WidgetListType);

  Object.entries(pointsData.points).forEach(([themeKey, theme]) => {
    result[themeKey] = {
      name: theme.name,
      sections: {},
    } as WidgetTheme;

    Object.entries(theme.sections).forEach(([sectionKey, section]) => {
      result[themeKey].sections[sectionKey] = {
        description: section.description,
        widgets: [],
      };

      const widgetPoints: Map<WidgetsTypes, WidgetPoints[]> = new Map();

      section.points.forEach((point: PointsProps) => {
        let assigned = false;

        if (widgetList.dateTime) {
          for (const dateTimeGroup of widgetList.dateTime) {
            const matchingTemplate = getMatchingTemplate(point, dateTimeGroup);
            if (matchingTemplate) {
              const widgetType: WidgetsTypes = 'dateTime';
              if (!widgetPoints.has(widgetType)) {
                widgetPoints.set(widgetType, []);
              }
              const widgetPoint: WidgetPoints = {
                ...point,
                timeUnit: (matchingTemplate as DateTimeItem).timeUnits,
              };
              widgetPoints.get(widgetType)?.push(widgetPoint);
              assigned = true;
              break;
            }
          }
        }

        if (!assigned && widgetList.rangeTime) {
          for (const rangeTimeGroup of widgetList.rangeTime) {
            const matchingTemplate = getMatchingTemplate(point, rangeTimeGroup);
            if (matchingTemplate) {
              const widgetType: WidgetsTypes = 'rangeTime';
              if (!widgetPoints.has(widgetType)) {
                widgetPoints.set(widgetType, []);
              }
              const widgetPoint: WidgetPoints = {
                ...point,
              };
              widgetPoints.get(widgetType)?.push(widgetPoint);
              assigned = true;
              break;
            }
          }
        }

        if (!assigned && widgetList.conditional) {
          const matchingTemplate = getMatchingTemplate(point, widgetList.conditional);
          if (matchingTemplate) {
            const widgetType: WidgetsTypes = 'conditional';
            if (!widgetPoints.has(widgetType)) {
              widgetPoints.set(widgetType, []);
            }
            const widgetPoint: WidgetPoints = {
              ...point,
            };
            widgetPoints.get(widgetType)?.push(widgetPoint);
            assigned = true;
          }
        }

        if (!assigned && widgetList.mapping) {
          const matchingTemplate = getMatchingTemplate(point, widgetList.mapping);
          if (matchingTemplate) {
            const widgetType: WidgetsTypes = 'mapping';
            if (!widgetPoints.has(widgetType)) {
              widgetPoints.set(widgetType, []);
            }
            const widgetPoint: WidgetPoints = {
              ...point,
            };
            widgetPoints.get(widgetType)?.push(widgetPoint);
            assigned = true;
          }
        }

        if (!assigned) {
          const widgetType: WidgetsTypes = 'default';
          if (!widgetPoints.has(widgetType)) {
            widgetPoints.set(widgetType, []);
          }
          const widgetPoint: WidgetPoints = {
            ...point,
          };
          widgetPoints.get(widgetType)?.push(widgetPoint);
        }
      });

      result[themeKey].sections[sectionKey].widgets = Array.from(widgetPoints.entries()).map(
        ([widget, points]) => {
          const widgetAndPoints: WidgetsTemplate = {
            widget,
            widgetLabel: WIDGET_TITLE_MAP[widget as keyof typeof WIDGET_TITLE_MAP],
            points,
          };

          if (widget === 'dateTime') {
            widgetAndPoints.make = equipmentMake;
          }
          return widgetAndPoints;
        }
      );
    });
  });

  return {
    ...pointsData,
    points: result,
  };
};

export const usePointsWidgets = (pointsData: PointsEntityState, site: Site) => {
  const [sectionsWidgets, setSectionsWidgets] = useState<WidgetThemesProps>({});

  let equipmentMake: keyof typeof EQUIPMENT_MAKE | undefined;
  site?.nodes.some((node) => {
    return node.equipment.some((equipment) => {
      const { equipmentType, make } = equipment;
      if (equipmentType === EQUIPMENT_TYPE.inverter) {
        if (make === EQUIPMENT_MAKE.avalon) {
          equipmentMake = EQUIPMENT_MAKE.avalon as keyof typeof EQUIPMENT_MAKE;
          return true;
        }
        if (make === EQUIPMENT_MAKE.envy) {
          equipmentMake = EQUIPMENT_MAKE.envy as keyof typeof EQUIPMENT_MAKE;
          return true;
        }
      }
      return false;
    });
  });

  useEffect(() => {
    const widgets = addWidgets(pointsData, equipmentMake);
    setSectionsWidgets(widgets.points);
  }, [pointsData, equipmentMake]);

  return sectionsWidgets;
};
